import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { TicketFiltersSidebar } from '../components/admin/support/TicketFiltersSidebar';
import { TicketListRow } from '../components/admin/support/TicketListRow';
import { BRAND, PRIORITY_ORDER, STATUS_ORDER, PRIORITY_LABELS, STATUS_LABELS, type TicketView, type TicketStatus, type TicketPriority } from '../components/admin/support/statuses';
import {
  fetchStats,
  fetchTickets,
  updateTicket,
  exportCsv,
  type TicketListItem,
  type TicketStats,
} from '../components/admin/support/ticketApi';

const PAGE_SIZE = 50;

export const AdminSupportInbox: React.FC = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState<TicketStats | null>(null);
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState<TicketView>('inbox');
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'web' | 'app' | 'email'>('all');
  const [promotedFilter, setPromotedFilter] = useState<'all' | 'promoted' | 'unpromoted'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  // Reset offset AND clear bulk selection when filters change. Without the
  // selection clear, previously-checked ticket IDs leak across views and
  // silently apply bulk actions to tickets the admin can no longer see.
  useEffect(() => {
    setOffset(0);
    setSelected(new Set());
  }, [view, statusFilter, priorityFilter, sourceFilter, promotedFilter, debouncedSearch]);

  const loadStats = useCallback(async () => {
    try {
      setStats(await fetchStats());
    } catch (e) {
      console.error('Failed to fetch stats', e);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTickets({
        view,
        status: (statusFilter || undefined) as TicketStatus | undefined,
        priority: (priorityFilter || undefined) as TicketPriority | undefined,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        promoted: promotedFilter === 'all' ? undefined : promotedFilter,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to fetch tickets', e);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [view, statusFilter, priorityFilter, sourceFilter, promotedFilter, debouncedSearch, offset]);

  useEffect(() => {
    loadStats();
    const id = setInterval(loadStats, 60_000);
    return () => clearInterval(id);
  }, [loadStats]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === tickets.length) return new Set();
      return new Set(tickets.map((t) => t.id));
    });
  }, [tickets]);

  const openTicket = useCallback(
    (id: string) => navigate(`/admin/support/${id}`),
    [navigate]
  );

  const handleBulkUpdate = useCallback(
    async (payload: { status?: TicketStatus; priority?: TicketPriority }) => {
      if (selected.size === 0 || bulkBusy) return;
      setBulkBusy(true);
      try {
        await Promise.all(
          Array.from(selected).map((id) => updateTicket(id, payload))
        );
        setSelected(new Set());
        await loadTickets();
        await loadStats();
      } catch (e) {
        console.error('Bulk update failed', e);
        alert('Bulk update failed. See console for details.');
      } finally {
        setBulkBusy(false);
      }
    },
    [selected, bulkBusy, loadTickets, loadStats]
  );

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  }, []);

  const pageFrom = offset + 1;
  const pageTo = Math.min(offset + tickets.length, offset + PAGE_SIZE);
  const hasMore = offset + tickets.length < total;

  const statsCards = useMemo(() => {
    if (!stats) return null;
    return [
      { label: 'New', value: stats.new_count, color: 'var(--status-info-text)', bg: 'var(--status-info-bg)' },
      { label: 'Open', value: stats.open_count, color: 'var(--status-cyan-text)', bg: 'var(--status-cyan-bg)' },
      { label: 'Waiting on reply', value: stats.pending_count, color: 'var(--status-warning-text)', bg: 'var(--status-warning-bg)' },
      { label: 'High + Urgent', value: stats.high_urgent_count, color: 'var(--status-error-text)', bg: 'var(--status-error-bg)' },
      { label: 'Unassigned', value: stats.unassigned_count, color: 'var(--text-strong)', bg: 'var(--surface-muted)' },
    ];
  }, [stats]);

  return (
    <div style={{ marginLeft: -24, marginRight: -24, marginTop: -24, minHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
      {/* Page header */}
      <div style={{ padding: '20px 32px 12px 32px', background: BRAND.surface, borderBottom: `1px solid ${BRAND.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: BRAND.text, margin: 0 }}>
              Support
            </h1>
            <p style={{ fontSize: 13, color: BRAND.textTertiary, marginTop: 3, marginBottom: 0 }}>
              Unified inbox for tickets from the app, website, and email replies
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleExport}
              style={{
                padding: '8px 16px',
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                color: BRAND.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ↓ Export CSV
            </button>
          </div>
        </div>

        {/* Stats cards */}
        {statsCards && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            {statsCards.map((c) => (
              <div
                key={c.label}
                style={{
                  background: BRAND.surface,
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 10,
                  padding: '14px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 600, color: BRAND.textTertiary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {c.label}
                </span>
                <span style={{ fontSize: 24, fontWeight: 700, color: c.color }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 28,
                      padding: '0 8px',
                      borderRadius: 6,
                      background: c.bg,
                    }}
                  >
                    {c.value}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main workspace: views + list */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <TicketFiltersSidebar activeView={view} onViewChange={setView} stats={stats} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: BRAND.surface }}>
          {/* Filter bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 24px',
              borderBottom: `1px solid ${BRAND.border}`,
              background: BRAND.surface,
            }}
          >
            <input
              type="text"
              placeholder="Search subject, email, name, or ticket #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 14px',
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                fontSize: 13,
                background: BRAND.surfaceAlt,
                outline: 'none',
                color: BRAND.text,
              }}
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={selectStyle}
            >
              <option value="">All statuses</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as any)}
              style={selectStyle}
            >
              <option value="">All priorities</option>
              {PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
              ))}
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as any)}
              style={selectStyle}
            >
              <option value="all">All sources</option>
              <option value="web">Web</option>
              <option value="app">App</option>
              <option value="email">Email</option>
            </select>

            <select
              value={promotedFilter}
              onChange={(e) => setPromotedFilter(e.target.value as any)}
              style={selectStyle}
              title="Filter by roadmap promotion state"
            >
              <option value="all">All roadmap</option>
              <option value="unpromoted">Unpromoted only</option>
              <option value="promoted">Promoted only</option>
            </select>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 24px',
                background: 'var(--status-info-bg-soft)',
                borderBottom: `1px solid var(--status-info-border)`,
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600, color: 'var(--status-info-text)' }}>
                {selected.size} selected
              </span>
              <span style={{ color: BRAND.textMuted, marginRight: 8 }}>·</span>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) handleBulkUpdate({ status: e.target.value as TicketStatus });
                  e.target.value = '';
                }}
                disabled={bulkBusy}
                style={{ ...selectStyle, background: BRAND.surface }}
              >
                <option value="">Change status…</option>
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) handleBulkUpdate({ priority: e.target.value as TicketPriority });
                  e.target.value = '';
                }}
                disabled={bulkBusy}
                style={{ ...selectStyle, background: BRAND.surface }}
              >
                <option value="">Change priority…</option>
                {PRIORITY_ORDER.map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
              <button
                onClick={() => setSelected(new Set())}
                style={{
                  marginLeft: 'auto',
                  padding: '4px 10px',
                  background: 'transparent',
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 6,
                  fontSize: 12,
                  color: BRAND.textSecondary,
                  cursor: 'pointer',
                }}
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Column header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '28px 72px 110px 100px 1fr 150px 100px',
              gap: 12,
              padding: '10px 16px',
              background: BRAND.surfaceAlt,
              borderBottom: `1px solid ${BRAND.border}`,
              fontSize: 10,
              fontWeight: 700,
              color: BRAND.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            <div>
              <input
                type="checkbox"
                checked={tickets.length > 0 && selected.size === tickets.length}
                onChange={toggleSelectAll}
                style={{ cursor: 'pointer', width: 14, height: 14, accentColor: BRAND.green }}
              />
            </div>
            <div>#</div>
            <div>Status</div>
            <div>Priority</div>
            <div>Subject</div>
            <div>Requester</div>
            <div style={{ textAlign: 'right' }}>Updated</div>
          </div>

          {/* Ticket rows */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 60, textAlign: 'center', color: BRAND.textMuted }}>
                Loading tickets…
              </div>
            ) : tickets.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: BRAND.textMuted }}>
                <div style={{ marginBottom: 8 }}><Inbox size={32} /></div>
                <div style={{ fontSize: 14 }}>No tickets match your filters</div>
              </div>
            ) : (
              tickets.map((t) => (
                <TicketListRow
                  key={t.id}
                  ticket={t}
                  selected={selected.has(t.id)}
                  onSelectToggle={toggleSelect}
                  onOpen={openTicket}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 24px',
                borderTop: `1px solid ${BRAND.border}`,
                fontSize: 12,
                color: BRAND.textTertiary,
                background: BRAND.surfaceAlt,
              }}
            >
              <span>
                Showing <strong style={{ color: BRAND.textSecondary }}>{pageFrom}–{pageTo}</strong> of <strong style={{ color: BRAND.textSecondary }}>{total}</strong>
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  style={pageBtnStyle(offset === 0)}
                >
                  ← Previous
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={!hasMore}
                  style={pageBtnStyle(!hasMore)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const selectStyle: React.CSSProperties = {
  padding: '7px 12px',
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  fontSize: 12,
  background: BRAND.surface,
  color: BRAND.textSecondary,
  cursor: 'pointer',
};

const pageBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  background: disabled ? BRAND.surfaceMuted : BRAND.surface,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  color: disabled ? BRAND.textMuted : BRAND.textSecondary,
  cursor: disabled ? 'not-allowed' : 'pointer',
});
